import React from 'react'
import {
  Box,
  Collapse,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
  type PaperProps,
  type SxProps,
  type Theme,
} from '@mui/material'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import { shapeBorderRadius } from '../../../theme/helpers'

const clampPositiveInt = (value: number, fallback: number): number => {
  if (!Number.isFinite(value)) {
    return fallback
  }
  const normalized = Math.max(1, Math.floor(value))
  return normalized
}

interface DashboardCardProps {
  title: string
  titleContent?: React.ReactNode
  page: number
  perPage: number
  total: number
  collapsed: boolean
  onToggleCollapse: (collapsed: boolean) => void
  onChangePage: (page: number) => void
  children: React.ReactNode
  loading?: boolean
  refreshing?: boolean
  headerAdornment?: React.ReactNode
  paperProps?: PaperProps
  onOpenSettings?: () => void
}

const DashboardCard: React.FC<DashboardCardProps> = ({
  title,
  titleContent,
  page,
  perPage,
  total,
  collapsed,
  onToggleCollapse,
  onChangePage,
  children,
  loading = false,
  refreshing = false,
  headerAdornment,
  paperProps,
  onOpenSettings,
}) => {
  const sanitizedPerPage = React.useMemo(() => clampPositiveInt(perPage, 5), [perPage])
  const sanitizedPage = React.useMemo(() => clampPositiveInt(page, 1), [page])
  const effectiveTotal = Math.max(0, Number.isFinite(total) ? Math.floor(total) : 0)
  const pageCount = Math.max(1, Math.ceil(effectiveTotal / sanitizedPerPage))
  const currentPage = Math.min(sanitizedPage, pageCount)
  const hasPrev = currentPage > 1
  const hasNext = currentPage < pageCount

  const start = effectiveTotal === 0 ? 0 : (currentPage - 1) * sanitizedPerPage + 1
  const end = effectiveTotal === 0 ? 0 : Math.min(effectiveTotal, currentPage * sanitizedPerPage)

  const handleToggleCollapse = () => {
    onToggleCollapse(!collapsed)
  }

  const handlePrev = () => {
    if (!hasPrev) {
      return
    }
    onChangePage(currentPage - 1)
  }

  const handleNext = () => {
    if (!hasNext) {
      return
    }
    onChangePage(currentPage + 1)
  }

  const { sx: paperSx, variant: _variant, ...restPaperProps } = paperProps ?? {}

  const combinedSx = React.useMemo<SxProps<Theme>>(() => {
    const base: SxProps<Theme> = {
      borderRadius: shapeBorderRadius,
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
      overflow: 'hidden',
      width: '100%',
      maxWidth: '100%',
      minWidth: 0,
      transition: (theme) =>
        theme.transitions.create(['box-shadow', 'border-color'], {
          duration: theme.transitions.duration.shorter,
        }),
    }

    if (!paperSx) {
      return base
    }

    if (Array.isArray(paperSx)) {
      return [base, ...paperSx]
    }

    return [base, paperSx]
  }, [paperSx])

  return (
    <Paper
      {...restPaperProps}
      variant="outlined"
      sx={combinedSx}
    >
      <Box
        component="header"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1,
          minHeight: 0,
        }}
      >
        <Box
          component="h3"
          sx={{
            flex: '1 1 auto',
            minWidth: 0,
            m: 0,
            typography: 'h6',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            minHeight: 0,
          }}
        >
          {titleContent ?? (
            <Typography
              variant="h6"
              component="span"
              noWrap
              sx={{ fontWeight: 'inherit', minWidth: 0 }}
            >
              {title}
            </Typography>
          )}
        </Box>
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          justifyContent="flex-end"
          sx={{ flex: '0 0 auto' }}
        >
          {headerAdornment}
          {onOpenSettings ? (
            <Tooltip title="Card settings">
              <IconButton aria-label="Open settings" size="small" onClick={onOpenSettings}>
                <SettingsOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
          <Tooltip title={collapsed ? 'Expand' : 'Collapse'}>
            <span>
              <IconButton aria-label={collapsed ? 'Expand' : 'Collapse'} size="small" onClick={handleToggleCollapse}>
                {collapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>
      {refreshing ? <LinearProgress color="primary" sx={{ height: 2 }} /> : null}
      <Box sx={{ flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Collapse in={!collapsed} appear timeout={200} unmountOnExit>
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            <Box
              sx={{
                flex: '1 1 auto',
                minHeight: 0,
                minWidth: 0,
                overflowY: 'auto',
                overflowX: 'hidden',
                px: 1,
                pt: 0,
                pb: 1,
              }}
            >
              {children}
            </Box>
            <Box
              component="footer"
              sx={{
                flex: '0 0 auto',
                px: 1,
                py: 0.75,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
              }}
            >
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Tooltip title="Previous page">
                  <span>
                    <IconButton aria-label="Previous page" size="small" onClick={handlePrev} disabled={!hasPrev || loading}>
                      <ChevronLeftIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 82, textAlign: 'center' }}>
                  {effectiveTotal === 0 ? '0 of 0' : `${start}–${end} of ${effectiveTotal}`}
                </Typography>
                <Tooltip title="Next page">
                  <span>
                    <IconButton aria-label="Next page" size="small" onClick={handleNext} disabled={!hasNext || loading}>
                      <ChevronRightIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            </Box>
          </Box>
        </Collapse>
      </Box>
    </Paper>
  )
}

export default DashboardCard
